import { z } from 'zod'
import { insightColorSchema } from './insight-spec'
import { streamColumnLabel } from './stream-columns'
import {
  GENERIC_SECTION_KEYS,
  inferSectionDisplay,
  isSectionArticle,
  type SectionArticle,
  type SectionDisplay,
} from './stream-sections'

/**
 * A build section as a dashboard module (CLI-758 follow-up): what it shows and how it is drawn,
 * kept on the block like a Derek chart's spec — the recipe, never the data, so it redraws from
 * whatever the build computed last. `view: 'auto'` is the default picked from the section's
 * shape (`autoSectionView`); anything else is someone's choice, made in the module's own menu or
 * by asking Derek ("the clusters as a donut, top 5").
 */

export const SECTION_VIEWS = [
  'auto',
  /** Headline figures as a strip of tiles. */
  'kpis',
  /** Slim ranked bars, one figure. */
  'bars',
  /** A compact table: name, its headline, two figures. */
  'table',
  /** Each row as its share of the whole. */
  'donut',
  /** Records of text, as small cards. */
  'cards',
  /** An echo chain's hops. */
  'chains',
  /** Counts over days, as columns. */
  'series',
] as const
export type SectionViewKind = (typeof SECTION_VIEWS)[number]

export const sectionViewSchema = z.object({
  /** The section by the build's key, or a part of it by path: `narratives`, `whitespace.outlets`. */
  key: z.string().min(1).max(120),
  view: z.enum(SECTION_VIEWS).default('auto'),
  /** Rows shown before "Show all". */
  limit: z.number().int().min(1).max(50).default(8),
  /** The figure the bars draw and a value sort orders by; null = the section's most telling. */
  value: z.string().max(80).nullable().default(null),
  /** `build` keeps the build's own order (it ranks its lists); `value` biggest first; `label` A–Z. */
  sort: z.enum(['build', 'value', 'label']).default('build'),
  /** Show each row's written summary (otherwise one click away). */
  summaries: z.boolean().default(false),
  /** Show the model-written headline under a row's name. */
  subtitles: z.boolean().default(true),
  /** The bars' colour; null = the brand's. */
  color: insightColorSchema.nullable().default(null),
})
export type SectionView = z.infer<typeof sectionViewSchema>

/** The section (or part) a view names, or undefined when the build has no such thing. */
export function sectionAt(sections: Record<string, unknown>, key: string): unknown {
  let value: unknown = sections
  for (const part of key.split('.')) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined
    value = (value as Record<string, unknown>)[part]
  }
  return value
}

/** The views a display can be drawn as — what the module's menu offers, and what Derek may pick. */
export function sectionViewsFor(display: SectionDisplay): SectionViewKind[] {
  switch (display.kind) {
    case 'ranking':
      return ['bars', 'table', 'donut', 'cards']
    case 'cards':
      return ['cards', 'table']
    case 'facts':
      return ['kpis', 'table']
    case 'chains':
      return ['chains']
    case 'series':
      return ['series', 'bars']
    case 'group':
      return ['auto']
    default:
      return ['auto']
  }
}

/**
 * The default view for a display, judged from its shape: headline figures as tiles, share-like
 * rows as slim bars, an entity named by what it is with a model-written headline as a compact
 * table, records of text as cards. A group (a section with parts) draws each part its own way.
 */
export function autoSectionView(display: SectionDisplay): Exclude<SectionViewKind, 'auto'> | null {
  switch (display.kind) {
    case 'facts':
      return 'kpis'
    case 'ranking':
      // A row with a headline of its own reads as a table line; bare figures as bars.
      return display.rows.some((row) => row.subtitle) ? 'table' : 'bars'
    case 'cards':
      return 'cards'
    case 'chains':
      return 'chains'
    case 'series':
      return 'series'
    default:
      return null
  }
}

/** The id of a section's module in the default rows — the web and Derek name it alike. */
export const sectionModuleId = (key: string) => `section-${key}`.slice(0, 100)

/** A section's title on a module card: the known sections by name, the rest from their key. */
const SECTION_TITLES: Record<string, string> = {
  analytics: 'Echoes',
  narratives: 'Narratives',
  narrative_clusters: 'Narrative clusters',
  narrative_clusters_td_coverage: 'Narrative clusters — own coverage',
  whitespace: 'Whitespace',
  jurisdictions: 'Jurisdictions',
  peer_competitor: 'Peer competitors',
  scholarly: 'Scholarly',
  upwork: 'Measurement',
}

export function sectionTitle(key: string): string {
  const [head, ...rest] = key.split('.')
  const base = SECTION_TITLES[head!] ?? streamColumnLabel(head!)
  return rest.length > 0 ? `${base} · ${rest.map(streamColumnLabel).join(' · ')}` : base
}

/** The sections a build wrote that make modules of their own: everything but the filter lists and
 *  `analytics` (its echo chains are what the Echoes module draws), and nothing empty. */
export function moduleSectionKeys(sections: Record<string, unknown>): string[] {
  return Object.entries(sections)
    .filter(
      ([key]) => key !== 'analytics' && !(GENERIC_SECTION_KEYS as readonly string[]).includes(key),
    )
    .filter(([, value]) => inferSectionDisplay(value).kind !== 'empty')
    .map(([key]) => key)
}

/** The build's echo chains — what the Echoes module draws — or null when the build writes none. */
export function buildEchoChains(
  sections: Record<string, unknown>,
): { article: SectionArticle; similarity: number | null }[][] | null {
  const echoes = sectionAt(sections, 'analytics.echoes')
  if (!Array.isArray(echoes) || echoes.length === 0) return null
  const display = inferSectionDisplay(echoes)
  if (display.kind !== 'chains') return null
  return display.chains.filter((chain) => chain.length > 1 && isSectionArticle(chain[0]!.article))
}
